import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import CustomDatePicker from './CustomDatePicker';
import PrimaryButton from '../Buttons/PrimaryButton';
import BorderedButton from '../Buttons/BorderedButton';
import CollapsiblePanel from '../CollapsiblePanel/CollapsiblePanel';
import CustomRadio from '../CustomRadio/CustomRadio';
import styles from './CustomerDetailsModal.module.css';
import rightStyles from '../RightSidebar/rightsidebar.module.css';
import "react-phone-number-input/style.css";
import { getCountryCallingCode, parsePhoneNumber, isValidPhoneNumber } from "react-phone-number-input";
import { getExampleNumber } from "libphonenumber-js";
import examples from "libphonenumber-js/mobile/examples";
import CountrySelectorWithSearch from "../PhoneInput/CountrySelectorWithSearch";
import { getInitials, getRandomColor } from '../../Utils/utils';
import { postCustomerData, getCustomerList } from '../../APICalling/APICalling';
import { fetchCountries, findCountryByCodeSync, getLocalPhoneDigits } from '../../Utils/countryUtils';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import logger from '../../Utils/logger';

const AddClientSidebar = ({ isOpen, onClose, onCustomerCreated }) => {
    const navigate = useNavigate();
    const toastContainerId = 'addClientSidebar';
    
	// Get placeholder based on selected country using libphonenumber-js
	const getMobilePlaceholder = (country) => {
		const countryCode = country && typeof country === 'object' ? country.code : country;
		if (!countryCode) return 'Mobile number';
		try {
			// Get example number for the country
			const exampleNumber = getExampleNumber(countryCode, examples);
			if (exampleNumber) {
				// Format the example number and remove country code
				const formatted = exampleNumber.formatNational();
				return formatted || 'Mobile number';
			}
		} catch (error) {
			logger.error('Error getting example number:', error);
		}
		return 'Mobile number';
	};

    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [email, setEmail] = useState('');
    const [phone, setPhone] = useState('');
    const [displayPhoneNumber, setDisplayPhoneNumber] = useState('');
    const [selectedCountry, setSelectedCountry] = useState('SG'); // Will be country object with {id, code, name, dial_code, image} or country code string
    const [gender, setGender] = useState('');
    const [dob, setDob] = useState(null);
    // Default view date for date of birth (25 years ago)
    const defaultViewDate = new Date();
    defaultViewDate.setFullYear(defaultViewDate.getFullYear() - 25);
    const [marketing, setMarketing] = useState(false);
    const [avatarColor, setAvatarColor] = useState('#9F9F9F'); // Default Neutral/500 color

    const [isLoading, setIsLoading] = useState(false);
    const [errors, setErrors] = useState({});
    const [existingUser, setExistingUser] = useState(null);

    // Focus first name field when modal opens
    useEffect(() => {
        if (isOpen) {
            // Small delay to ensure modal is fully rendered
            const timer = setTimeout(() => {
                const firstNameInput = document.getElementById('add-client-firstname');
                if (firstNameInput) {
                    firstNameInput.focus();
                }
            }, 100);
            return () => clearTimeout(timer);
        }
    }, [isOpen]);

    // Function to get custom initials based on input
    const getCustomInitials = (firstName, lastName) => {
        const first = firstName.trim();
        const last = lastName.trim();
        
        if (!first && !last) return '';
        
        if (first && !last) {
            // Only first name: show first and second letter of first name
            return first.length >= 2 ? first.substring(0, 2).toUpperCase() : first.toUpperCase();
        }
        
        if (first && last) {
            // Both names: show first letter of first name + first letter of last name
            return (first.charAt(0) + last.charAt(0)).toUpperCase();
        }
        
        if (!first && last) {
            // Only last name: show first letter of last name
            return last.charAt(0).toUpperCase();
        }
        
        return '';
    };

    // Function to update avatar based on name input
    const updateAvatar = (firstName, lastName) => {
        if (firstName.trim() || lastName.trim()) {
            // If any name is entered, generate a random color
            setAvatarColor(getRandomColor());
        } else {
            // If no name is entered, use default Neutral/500 color
            setAvatarColor('#6B7280');
        }
    };

    // Function to clear form
    const clearForm = () => {
        setFirstName('');
        setLastName('');
        setEmail('');
        setPhone('');
        setDisplayPhoneNumber('');
        // Reset to default country (Singapore)
        const defaultCountry = findCountryByCodeSync('SG');
        if (defaultCountry) {
            setSelectedCountry(defaultCountry);
        } else {
            setSelectedCountry('SG');
        }
        setGender('');
        setDob(null);
        setMarketing(false);
        setErrors({});
        setAvatarColor('#6B7280'); // Reset to default color
        setExistingUser(null); // Clear existing user info
    };

    // Warm the shared cache without overwriting a country entered while loading.
    useEffect(() => {
        // The selector displays loading errors and provides retry.
        fetchCountries().catch(() => {});
    }, []);

    // Clear display when phone is cleared. Do not rewrite display from nationalNumber
    // while typing — that caused digits (e.g. leading zeros) to be trimmed.
    // Country detection from full numbers is handled on paste / country selector.
    useEffect(() => {
        if (!phone) {
            setDisplayPhoneNumber('');
        }
    }, [phone]);



    // Function to handle click on existing user ID
    const handleExistingUserClick = (userId, userEmail) => {
        onClose(); // Close the current modal
        navigate(`/clients?search=${userEmail}`); // Navigate to clients page with email search
    };

    // Field-wise Validation
    const validateField = (name, value) => {
        let error = "";
        switch (name) {
            case "firstName":
                if (!value.trim()) error = "First name is required";
                break;
            case "lastName":
                // Last name is optional, no validation needed
                break;
            case "phone":
                if (!value) error = "Mobile number is required";
                else if (!isValidPhoneNumber(value)) error = "Invalid phone number";
                break;
            default:
                break;
        }
        setErrors((prevErrors) => ({ ...prevErrors, [name]: error }));
        return error === "";
    };

    // Validation for form submission
    const validateForm = () => {
        const firstNameValid = validateField("firstName", firstName);
        // Last name is optional, no validation needed
        const phoneValid = validateField("phone", phone);
        
        // Validate email format if provided
        if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
            setErrors((prevErrors) => ({ ...prevErrors, email: "Invalid email format" }));
            return false;
        }
        
        return firstNameValid && phoneValid;
    };

    const handleSave = async () => {
        // Validate all required fields
        if (!validateForm()) {
            return;
        }

        setIsLoading(true);

        try {
            await fetchCountries().catch(() => null);
            const countryCode = typeof selectedCountry === 'object' ? selectedCountry?.code : selectedCountry;
            const country = findCountryByCodeSync(countryCode);
            if (!country?.id) {
                setErrors((prev) => ({ ...prev, phone: 'Unable to load the selected country. Please try again.' }));
                return;
            }
            const loggedInUserId = Number(localStorage.getItem('user_id'));
            
            // Format DOB to YYYY-MM-DD using local date (not UTC) to avoid timezone shift
            let formattedDob = null;
            if (dob) {
                const year = dob.getFullYear();
                const month = String(dob.getMonth() + 1).padStart(2, '0');
                const day = String(dob.getDate()).padStart(2, '0');
                formattedDob = `${year}-${month}-${day}`;
            }
            
            const customerData = {
                name: firstName.trim(),
                last_name: lastName.trim(),
                email: email.trim() || null,
                contact_number: phone,
                membership: 0,
                gender: gender || null,
                dob: formattedDob,
                newsletter: marketing ? 1 : 0,
                panel: "outlet",
                status: 1,
                country_id: country.id,
                created_via : "outlet",
                created_by: loggedInUserId || null
            };

            const response = await postCustomerData(customerData);
            
            if (response?.data?.success) {
                // eslint-disable-next-line no-console
                // console.log('Customer added successfully:', response);
                // NOTE: keep sidebar open long enough for the toast to be visible.
                const autoCloseMs = 1500;
                toast.success('Client created successfully', { containerId: toastContainerId, autoClose: autoCloseMs });
                setTimeout(() => {
                    clearForm(); // Clear the form
                    onClose(); // Close the modal after toast is visible
                    // Notify parent component to refresh customer list
                    if (onCustomerCreated) {
                        onCustomerCreated();
                    }
                }, autoCloseMs + 200);
            } else {
                // Handle API errors
                console.error('Failed to add customer:', response);
                const apiErrors = response?.errors || {};
                
                // Show toast for specific error types
                if (apiErrors.email) {
                    const emailError = apiErrors.email[0];
                    // console.log('Email error detected:', emailError);
                    
                    if (emailError.toLowerCase().includes('already taken') || emailError.toLowerCase().includes('already exists') || emailError.toLowerCase().includes('has already been taken')) {
                        // console.log('Email already taken detected, setting dummy user');
                        // Generate random 4-digit number
                        const randomId = Math.floor(1000 + Math.random() * 9000);
                        // Show dummy ID in error message
                        setExistingUser({ id: randomId.toString(), email: email.trim() }); // Dummy user data
                        // Don't show toast, just set the error message
                    } else {
                        // console.log('Other email error, showing toast');
                        toast.error(emailError);
                    }
                } else if (apiErrors.contact_number) {
                    toast.error(apiErrors.contact_number[0]);
                } else if (apiErrors.name) {
                    toast.error(apiErrors.name[0]);
                } else if (apiErrors.last_name) {
                    toast.error(apiErrors.last_name[0]);
                } else {
                    // Generic error message
                    toast.error('Failed to create customer. Please try again.');
                }
                
                // Map API errors to our field names for form display
                const mappedErrors = {};
                if (apiErrors.name) mappedErrors.firstName = apiErrors.name[0];
                if (apiErrors.last_name) mappedErrors.lastName = apiErrors.last_name[0];
                if (apiErrors.contact_number) mappedErrors.phone = apiErrors.contact_number[0];
                if (apiErrors.email) {
                    // console.log('Mapping email error:', apiErrors.email[0]);
                    // For email already taken error, show clickable message
                    if (apiErrors.email[0].toLowerCase().includes('already taken') || apiErrors.email[0].toLowerCase().includes('already exists') || apiErrors.email[0].toLowerCase().includes('has already been taken')) {
                        // console.log('Setting clickable error message');
                        // Generate random 4-digit number
                        const randomId = Math.floor(1000 + Math.random() * 9000);
                        mappedErrors.email = `The email has already been taken.\nClick here to view User ID : ${randomId}`;
                    } else {
                        // console.log('Setting normal error message');
                        mappedErrors.email = apiErrors.email[0];
                    }
                }
                setErrors(mappedErrors);
            }
        } catch (error) {
        // eslint-disable-next-line no-console
            console.error('Error adding customer:', error);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className={`${styles.sidebarOverlay} ${isOpen ? styles.open : ''}`} onClick={onClose}>
            {createPortal(
                <ToastContainer position="top-right" autoClose={3000} containerId={toastContainerId} />,
                document.body
            )}
            <div 
                className={`${styles.sidebarContent} ${styles.addClient}`} 
                onClick={(e) => e.stopPropagation()}
            >
                <div className={styles.sidebarHeader}>
                    <div className={styles.headerLeft}>
                        <span className={styles.closeText} onClick={onClose}>× Client Details</span>
                    </div>
                    {/* <div className={styles.headerRight}>
                        <button className={styles.moreButton}>⋮</button>
                    </div> */}
                </div>

                <div className={styles.sidebarBody}>
                    {/* Left form panel */}
                    <div className={styles.leftPanel}>
                        <div className={styles.customerProfile}>
                            <div className={styles.profileImage} style={{ backgroundColor: avatarColor }}>
                                {getCustomInitials(firstName, lastName)}
                            </div>
                        </div>

                        <form 
                            className={styles.formContent}
                            onSubmit={(e) => e.preventDefault()}
                            name="add-client-form"
                            id="add-client-form"
                            autoComplete="off"
                        >
                        <div className={styles.formGroup}>
                            <input
                                type="text"
                                value={firstName}
                                onChange={(e) => {
                                    e.stopPropagation();
                                    const value = e.target.value;
                                    setFirstName(value);
                                    validateField("firstName", value);
                                    updateAvatar(value, lastName);
                                }}
                                placeholder="First Name"
                                className={rightStyles.iconFormControl}
                                disabled={isLoading}
                                autoComplete="given-name"
                                name="given-name"
                                id="add-client-firstname"
                                data-form-type="add-client"
                                autoFocus
                            />
                            {errors.firstName && (
                                <div className={`${rightStyles.errorMessage} ${styles.addClientError}`}>
                                    {errors.firstName}
                                </div>
                            )}
                        </div>
                        <div className={styles.formGroup}>
                            <input
                                type="text"
                                value={lastName}
                                onChange={(e) => {
                                    e.stopPropagation();
                                    const value = e.target.value;
                                    setLastName(value);
                                    validateField("lastName", value);
                                    updateAvatar(firstName, value);
                                }}
                                placeholder="Last Name"
                                className={rightStyles.iconFormControl}
                                disabled={isLoading}
                                autoComplete="family-name"
                                name="family-name"
                                id="add-client-lastname"
                                data-form-type="add-client"
                            />
                            {errors.lastName && (
                                <div className={`${rightStyles.errorMessage} ${styles.addClientError}`}>
                                    {errors.lastName}
                                </div>
                            )}
                        </div>
                        <div className={styles.formGroup}>
                            <input
                                type="email"
                                value={email}
                                onChange={(e) => {
                                    e.stopPropagation();
                                    setEmail(e.target.value);
                                }}
                                placeholder="Email"
                                className={rightStyles.iconFormControl}
                                disabled={isLoading}
                                autoComplete="email"
                                name="email"
                                id="add-client-email"
                                data-form-type="add-client"
                            />
                            {errors.email && (
                                <div className={`${rightStyles.errorMessage} ${styles.addClientError}`}>
                                    {/* {console.log('Rendering email error:', errors.email)} */}
                                    {errors.email.toLowerCase().includes('click here to view user id') ? (
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                            <span style={{ color: '#DC2626' }}>The email has already been taken.</span>
                                            <span 
                                                style={{ 
                                                    color: '#9F9F9F', 
                                                    cursor: 'pointer', 
                                                    fontWeight: '500'
                                                }}
                                                onClick={() => {
                                                    // Extract the random ID from the error message
                                                    const idMatch = errors.email.match(/ ID : (\d+)/);
                                                    const randomId = idMatch ? idMatch[1] : '1234';
                                                    handleExistingUserClick(randomId, email.trim());
                                                }}
                                            >
                                                Click here to view, ID : {errors.email.match(/ ID : (\d+)/)?.[1] || '1234'}
                                            </span>
                                        </div>
                                    ) : (
                                        errors.email
                                    )}
                                </div>
                            )}

                        </div>
                        <div className={`${styles.formGroup} ${styles.phoneInputFormGroup}`}>
                            <div 
                                className={`${rightStyles.phoneInputWrapper} ${rightStyles.phoneInputWrapperCustom} hide-default-country-selector`}
                            >
                                <CountrySelectorWithSearch
                                    value={selectedCountry}
                                    onChange={(country) => {
                                        // country is now an object with {id, code, name, dial_code, image}
                                        setSelectedCountry(country);
                                        // Update phone number with new country code if display number exists
                                        const digitsOnly = displayPhoneNumber.replace(/\D/g, '');
                                        if (digitsOnly && country?.dial_code) {
                                            const newNumber = `+${country.dial_code}${digitsOnly}`;
                                            setPhone(newNumber);
                                            validateField("phone", newNumber);
                                        }
                                    }}
                                    disabled={isLoading}
                                />
                                <div className={rightStyles.phoneInputOnly}>
                                    <input
                                        type="tel"
                                        value={displayPhoneNumber}
                                        onKeyDown={(e) => {
                                            if (e.key === '+' || e.key === '=') {
                                                e.preventDefault();
                                            }
                                        }}
                                        onPaste={(e) => {
                                            e.preventDefault();
                                            const pastedText = e.clipboardData.getData('text');

                                            // Paste with +: detect country and show national digits
                                            if (pastedText.startsWith('+')) {
                                                try {
                                                    const phoneNumber = parsePhoneNumber(pastedText);
                                                    if (phoneNumber && phoneNumber.country) {
                                                        const countryObject = findCountryByCodeSync(phoneNumber.country);
                                                        if (countryObject) {
                                                            setSelectedCountry(countryObject);
                                                        } else {
                                                            setSelectedCountry(phoneNumber.country);
                                                        }
                                                        setDisplayPhoneNumber(getLocalPhoneDigits(pastedText, phoneNumber));
                                                        setPhone(pastedText);
                                                        validateField("phone", pastedText);
                                                        return;
                                                    }
                                                } catch (err) {
                                                    // If parsing fails, continue with digits-only flow
                                                }
                                            }

                                            // Non-international paste: digits only with selected country
                                            const digitsOnly = pastedText.replace(/\D/g, '');
                                            if (digitsOnly) {
                                                const dialCode = selectedCountry && typeof selectedCountry === 'object'
                                                    ? selectedCountry.dial_code
                                                    : getCountryCallingCode(selectedCountry);
                                                const formattedValue = `+${dialCode}${digitsOnly}`;
                                                setPhone(formattedValue);
                                                setDisplayPhoneNumber(digitsOnly);
                                                validateField("phone", formattedValue);
                                            }
                                        }}
                                        onChange={(e) => {
                                            // Digits only — no letters, spaces, or +
                                            const digitsOnly = e.target.value.replace(/\D/g, '');
                                            setDisplayPhoneNumber(digitsOnly);

                                            if (!digitsOnly) {
                                                setPhone('');
                                                validateField("phone", '');
                                                return;
                                            }

                                            const dialCode = selectedCountry && typeof selectedCountry === 'object'
                                                ? selectedCountry.dial_code
                                                : getCountryCallingCode(selectedCountry);
                                            const formattedValue = `+${dialCode}${digitsOnly}`;
                                            setPhone(formattedValue);
                                            validateField("phone", formattedValue);
                                        }}
                                        placeholder={getMobilePlaceholder(selectedCountry || 'SG')}
                                        className={`${rightStyles.iconFormControl} ${rightStyles.phoneInputCustom}`}
                                        disabled={isLoading}
                                        autoComplete="tel"
                                        name="tel"
                                        id="add-client-phone"
                                        data-form-type="add-client"
                                        style={{ paddingLeft: '0' }}
                                    />
                                </div>
                            </div>
                            {errors.phone && (
                                <div className={`${rightStyles.errorMessage} ${styles.addClientError}`}>
                                    {errors.phone}
                                </div>
                            )}
                        </div>

                        {/* Additional Details Section */}
                            <div className={styles.formSection}>
                            <div className={styles.sectionTitle}>Additional Details</div>
                                <div className={styles.groupInput}>
                                    <CustomRadio
                                        label="Female"
                                        name="gender"
                                        value="female"
                                        checked={gender === 'female'}
                                        onChange={(e) => {setGender(gender === e.target.value ? '' : e.target.value);}}
                                        disabled={isLoading}
                                    />
                                    <CustomRadio
                                        label="Male"
                                        name="gender"
                                        value="male"
                                        checked={gender === 'male'}
                                        onChange={(e) => { setGender(gender === e.target.value ? '' : e.target.value);}}
                                        disabled={isLoading}
                                    />
                                    <CustomRadio
                                        label="Non-binary"
                                        name="gender"
                                        value="non-binary"
                                        checked={gender === 'non-binary'}
                                        onChange={(e) => { setGender(gender === e.target.value ? '' : e.target.value); }}
                                        disabled={isLoading}
                                    />
                                    <CustomRadio
                                        label="Prefer not to say"
                                        name="gender"
                                        value="prefer-not"
                                        checked={gender === 'prefer-not'}
                                        onChange={(e) => {setGender(gender === e.target.value ? '' : e.target.value);}}
                                        disabled={isLoading}
                                    />
                                </div>

                                <div className={styles.formGroup}>
                                <CustomDatePicker
                                            selected={dob}
                                            onChange={(date) => setDob(date)}
                                            dateFormat="dd MMM yyyy"
                                    placeholderText="Date of Birth"
                                    className={rightStyles.iconFormControl}
                                    disabled={isLoading}
                                    showYearDropdown
                                    dropdownMode="select"
                                    yearDropdownItemNumber={100}
                                    scrollableYearDropdown
                                    maxDate={new Date()}
                                    minDate={new Date(1900, 0, 1)}
                                    openToDate={defaultViewDate}
                                        />
                                    </div>
                                </div>

                        {/* Messaging Preferences Section */}
                        <div className={styles.formSection}>
                            <div className={styles.sectionTitle}>Messaging Preferences</div>
                            <div className={styles.formGroup}>
                                <label className={styles.toggleRow}>
                                    <span className={styles.inputLabel}>Marketing</span>
                                    <label className={rightStyles.switch}>
                                        <input
                                            type="checkbox"
                                            checked={marketing}
                                            onChange={(e) => setMarketing(e.target.checked)}
                                            disabled={isLoading}
                                        />
                                        <span className={rightStyles.sliderClinetPage}></span>
                                    </label>
                                </label>
                            </div>
                        </div>

                                                </form> {/* Close formContent */}

                        <div className={styles.formFooter}>
                            <BorderedButton text="Cancel" onClick={onClose} className={`${styles.footerBtn} ${styles.footerBtnCancel}`} disabled={isLoading} />
                            <PrimaryButton 
                                text={isLoading ? "Saving..." : "Save"} 
                                onClick={handleSave} 
                                className={`${styles.footerBtn} ${styles.footerBtnPrimary}`}
                                disabled={isLoading}
                            />
                        </div>
                    </div>

                    {/* Right empty panel to match layout */}
                    <div className={styles.rightPanel}></div>
                </div>
            </div>
        </div>
    );
};

export default AddClientSidebar;
